import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import MoleculeImage from './MoleculeImage';
import StructureEditor from './StructureEditor';
import API_BASE from '../config';
import { getEditPassword, setEditPassword, authHeaders } from '../services/auth';
import './InnDetail.css';

const EDITABLE_FIELDS = [
  { key: 'inn_en',            label: 'INN name' },
  { key: 'description_en',    label: 'Description' },
  { key: 'cas_number',        label: 'CAS' },
  { key: 'smiles',            label: 'SMILES', long: true },
];

const InnDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [compound, setCompound] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);

  // Edit state
  const [editing, setEditing]   = useState(false);
  const [draft, setDraft]       = useState({});
  const [pwdInput, setPwdInput] = useState('');
  const [pwdError, setPwdError] = useState('');
  const [needsPwd, setNeedsPwd] = useState(false);
  const [pwdAction, setPwdAction] = useState('text'); // 'text' | 'structure'
  const [saving, setSaving]       = useState(false);
  const [saveMsg, setSaveMsg]     = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editingStructure, setEditingStructure] = useState(false);
  const [editorMolblock, setEditorMolblock] = useState('');
  const [imageVersion, setImageVersion] = useState(0);

  useEffect(() => {
    fetch(`${API_BASE}/inn/${id}`)
      .then(r => r.json())
      .then(data => { setCompound(data); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  }, [id]);

  const enterEdit = () => {
    if (getEditPassword()) {
      setDraft({ ...compound });
      setEditing(true);
    } else {
      setPwdAction('text');
      setNeedsPwd(true);
    }
  };

  const openStructureEditor = async () => {
    if (compound.mol_block) {
      setEditorMolblock(compound.mol_block);
      setEditingStructure(true);
      return;
    }
    // No saved pose yet — ask RDKit (which already parses this compound's
    // stereo correctly everywhere else in the app) to generate a proper
    // wedge-bonded molblock, rather than handing Ketcher the raw SMILES.
    try {
      const resp = await fetch(`${API_BASE}/smiles-to-molblock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ smiles: compound.smiles }),
      });
      const data = await resp.json();
      setEditorMolblock(resp.ok ? data.molblock : '');
    } catch {
      setEditorMolblock('');
    }
    setEditingStructure(true);
  };

  const enterStructureEdit = () => {
    if (getEditPassword()) {
      openStructureEditor();
    } else {
      setPwdAction('structure');
      setNeedsPwd(true);
    }
  };

  const handleUnlock = async (e) => {
    e.preventDefault();
    const pwd = pwdInput.trim();
    if (!pwd) return;
    const resp = await fetch(`${API_BASE}/auth/check`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Edit-Password': pwd },
    });
    if (resp.ok) {
      setEditPassword(pwd);
      setPwdError('');
      setNeedsPwd(false);
      if (pwdAction === 'structure') {
        openStructureEditor();
      } else {
        setDraft({ ...compound });
        setEditing(true);
      }
    } else {
      setPwdError('Wrong password');
    }
  };

  const handleStructureSave = async (newSmiles, newMolblock) => {
    setSaving(true);
    setSaveMsg(null);
    const resp = await fetch(`${API_BASE}/inn/${id}/structure`, {
      method: 'PATCH',
      headers: authHeaders(),
      body: JSON.stringify({ smiles: newSmiles, mol_block: newMolblock }),
    });
    if (resp.ok) {
      setCompound(c => ({ ...c, smiles: newSmiles, mol_block: newMolblock }));
      setImageVersion(v => v + 1);
      setEditingStructure(false);
      setSaveMsg('Saved');
      setTimeout(() => setSaveMsg(null), 2000);
    } else {
      const data = await resp.json();
      setSaveMsg(data.error || 'Save failed');
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    const resp = await fetch(`${API_BASE}/inn/${id}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    if (resp.ok) {
      navigate('/inn');
    } else {
      const data = await resp.json();
      setSaveMsg(data.error || 'Delete failed');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveMsg(null);
    const changes = {};
    EDITABLE_FIELDS.forEach(({ key }) => {
      if ((draft[key] ?? '') !== (compound[key] ?? '')) changes[key] = draft[key] ?? '';
    });
    if (!Object.keys(changes).length) {
      setEditing(false);
      setSaving(false);
      return;
    }
    const resp = await fetch(`${API_BASE}/inn/${id}`, {
      method: 'PATCH',
      headers: authHeaders(),
      body: JSON.stringify(changes),
    });
    if (resp.ok) {
      setCompound(c => ({ ...c, ...changes }));
      setEditing(false);
      setSaveMsg('Saved');
      setTimeout(() => setSaveMsg(null), 2000);
    } else {
      const data = await resp.json();
      setSaveMsg(data.error || 'Save failed');
    }
    setSaving(false);
  };

  if (loading) return <div className="inn-detail-loading">Loading…</div>;
  if (error)   return <div className="inn-detail-error">Error: {error}</div>;
  if (!compound) return null;

  return (
    <div className="inn-detail-container">
      <div className="inn-detail-back">
        <Link to="/inn">← Back to INN Browser</Link>
      </div>

      <div className="inn-detail-card">
        <div className="inn-detail-header">
          <div>
            <h1 className="inn-detail-name">{compound.inn_en || compound.latin_name}</h1>
            {compound.description_en && (
              <div className="inn-detail-class">{compound.description_en}</div>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {saveMsg && <span className={`fetch-smiles-msg ${saveMsg === 'Saved' ? 'success' : 'error'}`}>{saveMsg}</span>}
            <span className="inn-detail-badge">PL-{compound.list_number}</span>
            {!editing && compound.smiles && (
              <button className="edit-btn" onClick={enterStructureEdit}>Edit structure</button>
            )}
            {!editing && (
              <button className="edit-btn" onClick={enterEdit}>Edit</button>
            )}
          </div>
        </div>

        {/* Password prompt */}
        {needsPwd && (
          <form className="pwd-form" onSubmit={handleUnlock}>
            <input
              type="password"
              placeholder="Edit password"
              value={pwdInput}
              onChange={e => setPwdInput(e.target.value)}
              autoFocus
              className="pwd-input"
            />
            <button type="submit" className="fetch-smiles-btn">Unlock</button>
            <button type="button" className="cancel-btn" onClick={() => setNeedsPwd(false)}>Cancel</button>
            {pwdError && <span className="fetch-smiles-msg error">{pwdError}</span>}
          </form>
        )}

        {/* Edit form */}
        {editing ? (
          <div className="edit-form">
            {EDITABLE_FIELDS.map(({ key, label, long }) => (
              <div key={key} className="edit-row">
                <label className="edit-label">{label}</label>
                {long ? (
                  <textarea
                    className="edit-textarea"
                    value={draft[key] ?? ''}
                    onChange={e => setDraft(d => ({ ...d, [key]: e.target.value }))}
                  />
                ) : (
                  <input
                    className="edit-input"
                    value={draft[key] ?? ''}
                    onChange={e => setDraft(d => ({ ...d, [key]: e.target.value }))}
                  />
                )}
              </div>
            ))}
            <div className="edit-actions">
              <button className="fetch-smiles-btn" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button className="cancel-btn" onClick={() => { setEditing(false); setConfirmDelete(false); }}>Cancel</button>
              <div className="delete-area">
                {!confirmDelete ? (
                  <button className="delete-btn" onClick={() => setConfirmDelete(true)}>Delete record</button>
                ) : (
                  <>
                    <span className="delete-confirm-text">Are you sure?</span>
                    <button className="delete-btn-confirm" onClick={handleDelete}>Yes, delete</button>
                    <button className="cancel-btn" onClick={() => setConfirmDelete(false)}>No</button>
                  </>
                )}
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="inn-detail-body">
              <div className="inn-detail-structure">
                {compound.smiles ? (
                  <MoleculeImage smiles={compound.smiles} innId={compound.id} version={imageVersion} width={400} height={320} />
                ) : (
                  <div className="inn-detail-no-structure">
                    <span className="biologic-badge-large">Biologic</span>
                    <p>No small-molecule structure available</p>
                  </div>
                )}
                {compound.smiles && (
                  <div className="inn-detail-smiles">{compound.smiles}</div>
                )}
              </div>

              <div className="inn-detail-meta">
                {compound.cas_number && (
                  <div className="inn-detail-row">
                    <span className="detail-label">CAS</span>
                    <span className="detail-value">{compound.cas_number}</span>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {editingStructure && (
        <StructureEditor
          smiles={compound.smiles}
          molblock={editorMolblock}
          onSave={handleStructureSave}
          onClose={() => setEditingStructure(false)}
        />
      )}
    </div>
  );
};

export default InnDetail;
